import {
  Quotation,
  QuotationSummaryResponse,
  ProductConfiguration,
  Discount,
  QuotationCoverage,
} from 'data-layer-cr/dist/models';
import {
  appliesDiscount,
  isInspectionRequired,
  defaultUnitEntity,
} from 'data-layer-cr/dist/functions/quotations';
import {
  COMPLETE_VEHICLE,
  THIRD_PARTY_PACK,
  THIRD_PARTY_VEHICLE,
} from 'data-layer-cr/dist/constants/products';
import { LEGAL_PERSON } from 'data-layer-cr/dist/constants/customerTypes';
import { AUTOPX_DISCOUNT_ID } from 'data-layer-cr/dist/constants/discounts';

import {
  GOLD_ENDORSEMENT,
  PLAT_ENDORSEMENT,
  PLAT_PLUS_ENDORSEMENT,
} from 'src/constants/endorsementCodes';

// Constants
import {
  forVehicle,
  forNewVehicle,
  forVehicleRC,
  forLegalEntity,
  forLegalEntityNewCCRC,
  forLegalEntityRC,
} from '../constants/emissionWelcomeInstructions';
import { creditCard, achPayment } from '../constants/paymentTypes';
import EmissionWelcomeInstructions from '../models/EmissionWelcomeInstruction';

/**
 * Returns an array that represents the list of documents that the quotation requires.
 *
 * @param quotation
 */
export function requiredDocumentsForEmission(
  quotation: Quotation,
  configuration: ProductConfiguration,
): EmissionWelcomeInstructions {
  if (quotation.customerType === LEGAL_PERSON) {
    if ([THIRD_PARTY_PACK, THIRD_PARTY_VEHICLE].includes(quotation.productId)) {
      if (defaultUnitEntity(quotation).isNewCar) {
        return forLegalEntityNewCCRC;
      }
      return forLegalEntityRC;
    }
    if (isInspectionRequired(quotation, configuration.inspectionRequired)) {
      return forLegalEntity;
    }
    return forLegalEntityNewCCRC;
  }
  if ([THIRD_PARTY_PACK, THIRD_PARTY_VEHICLE].includes(quotation.productId)) {
    if (defaultUnitEntity(quotation).isNewCar) {
      return forNewVehicle;
    }
    return forVehicleRC;
  }
  if (isInspectionRequired(quotation, configuration.inspectionRequired)) {
    return forVehicle;
  }
  return forNewVehicle;
}

/**
 * This method returns true if the quotation allows for discount, and the selected payment also applies
 *
 * @param quotation - The quotation to check
 * @param paymentType - The selected payment method
 */
export function appliesDiscountForPayment(
  quotation: Quotation,
  paymentType: string,
): boolean {
  const conditions = [creditCard, achPayment];
  return appliesDiscount(quotation) && conditions.includes(paymentType);
}

/**
 * Returns true if we must assume that the summary has a product that displays the
 * total insured value
 *
 * @param summary - The summary to check
 */
export function shouldShowTotalAmount(
  summary: QuotationSummaryResponse,
): boolean {
  return summary.productType === COMPLETE_VEHICLE;
}

/**
 * Will return true if the provided quotation allows the broker to realize the payment.
 *
 * @param quotation - The quotation to check
 */
export function allowsBrokerPayment(quotation: Quotation): boolean {
  return (
    typeof quotation.paymentMethods !== 'undefined' &&
    quotation.paymentMethods !== null &&
    quotation.paymentMethods.remittance
  );
}

export const findAutoPxIndex = (quotationDiscounts: Discount[]): number =>
  quotationDiscounts.findIndex(
    (discount) => discount.id === AUTOPX_DISCOUNT_ID,
  );

export const findAutoPxUnit = (
  quotationDiscounts: Discount[] | null,
): Discount | undefined => {
  if (quotationDiscounts) {
    return quotationDiscounts.find(
      (discount) => discount.id === AUTOPX_DISCOUNT_ID,
    );
  }
  return undefined;
};

/**
 * Returns the description of the selected endorsment
 * @param coverages
 */
export function getSelectedEndorsment(coverages: QuotationCoverage[]): string {
  if (coverages.find((coverage) => coverage.coverageId === GOLD_ENDORSEMENT)) {
    return 'Dorado Plus';
  }
  if (coverages.find((coverage) => coverage.coverageId === PLAT_ENDORSEMENT)) {
    return 'Platino';
  }

  if (
    coverages.find((coverage) => coverage.coverageId === PLAT_PLUS_ENDORSEMENT)
  ) {
    return 'Platino Plus';
  }
  return '';
}

/**
 * Returns the name of the customer
 * @param quotation
 */
export function getCustomerName(quotation: Quotation): string {
  if (quotation.customerType === LEGAL_PERSON) {
    return quotation.legalEntity !== null ? quotation.legalEntity?.name : '';
  }
  return `${quotation.customer.firstName} ${quotation.customer.lastName}`;
}
